import { useState, useEffect, useCallback } from 'react';
import { documentService } from '@/services/documentService';
import type { DriverDocument, DocumentType } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useDocuments() {
  const { user } = useUnifiedAuth();
  const [documents, setDocuments] = useState<DriverDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const [docs, types] = await Promise.all([
        documentService.list(),
        documentService.getDocumentTypes(),
      ]);
      setDocuments(docs);
      setDocumentTypes(types);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading documents');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  const upload = useCallback(async (file: File, docTypeId: string, vehicleId?: string) => {
    const path = `${user!.id}/${Date.now()}-${file.name}`;
    const fileUrl = await documentService.uploadFile(file, path);
    const doc = await documentService.upload({
      document_type_id: docTypeId,
      file_url: fileUrl,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      vehicle_id: vehicleId,
    });
    setDocuments(prev => [doc, ...prev]);
    return doc;
  }, [user]);

  const validate = useCallback(async (id: string) => {
    const doc = await documentService.validate(id);
    setDocuments(prev => prev.map(d => d.id === id ? doc : d));
    return doc;
  }, []);

  return { documents, documentTypes, loading, error, refetch: fetch, upload, validate };
}
